import { Link, useParams } from '@tanstack/react-router';
import { ArrowLeft, MapPin, Globe } from 'lucide-react';
import { useDiveSite } from '@/data/dive-sites';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { useDocumentTitle } from '@/lib/useDocumentTitle';

export function DiveSiteDetailPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const { data: site, isLoading } = useDiveSite(Number(id));

  useDocumentTitle(site?.title ?? 'Tauchplatz');

  if (isLoading || !site) {
    return <div className="grid h-full place-items-center p-8"><Spinner className="h-8 w-8" /></div>;
  }

  return (
    <div className="space-y-6 p-4 sm:p-6 md:p-8">
      <Link to="/sites" className="inline-flex items-center gap-1 text-sm text-[var(--muted-foreground)] hover:text-ocean-700">
        <ArrowLeft className="h-4 w-4" /> Zurück
      </Link>

      <header>
        <h1 className="text-3xl font-semibold">{site.title}</h1>
        {site.regionPathStr && (
          <p className="mt-1 flex items-center gap-1 text-sm text-[var(--muted-foreground)]">
            <MapPin className="h-4 w-4" /> {site.regionPathStr}
          </p>
        )}
      </header>

      <Card>
        <CardHeader><CardTitle>Details</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <Stat label="Quelle"        value={site.source.replace('_', ' ')} />
          <Stat label="Land"          value={site.countryCode} />
          <Stat label="Site-Typ"      value={site.siteType} />
          <Stat label="Schwierigkeit" value={site.difficulty} />
          <Stat label="Einstieg"      value={site.entryType} />
          <Stat label="Max. Tiefe"    value={site.maxDepthMeters ? `${site.maxDepthMeters} m` : null} />
          <Stat label="Verifiziert"   value={site.verified ? 'ja' : 'nein'} />
        </CardContent>
      </Card>

      {(site.geoLatitude != null && site.geoLongitude != null) && (
        <Card>
          <CardHeader><CardTitle>Lage</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <p className="font-mono text-sm">{site.geoLatitude.toFixed(5)}, {site.geoLongitude.toFixed(5)}</p>
            <a
              href={`https://www.google.com/maps?q=${site.geoLatitude},${site.geoLongitude}`}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-1 text-sm text-ocean-700 hover:underline"
            >
              <Globe className="h-4 w-4" /> In Google Maps öffnen
            </a>
          </CardContent>
        </Card>
      )}

      {site.description && (
        <Card>
          <CardHeader><CardTitle>Beschreibung</CardTitle></CardHeader>
          <CardContent><p className="whitespace-pre-line text-sm">{site.description}</p></CardContent>
        </Card>
      )}

      {site.source !== 'user' && (
        <p className="text-xs text-[var(--muted-foreground)]">
          Site-Daten ursprünglich aus{' '}
          {site.source === 'osm' && (
            <a href="https://www.openstreetmap.org/" className="underline" target="_blank" rel="noreferrer">© OpenStreetMap contributors</a>
          )}
          {site.source === 'dive_vibe' && (
            <a href="https://github.com/jbunderwater/dive-vibe-community" className="underline" target="_blank" rel="noreferrer">dive-vibe-community</a>
          )}
          {' '}(ODbL 1.0).
        </p>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string | number | null | undefined }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-[var(--muted-foreground)]">{label}</p>
      <p className="text-base">{value ?? '–'}</p>
    </div>
  );
}
